/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import { isObjectOrArrayAndEmpty } from '../../../shared/Utils/Utils';
import ConnectionError from '../../../shared/ErrorComponents/ConnectionError';
import withLoadingStatus from '../../../shared/StatusWrappers/LoadingStatus';
import withErrorStatus from '../../../shared/StatusWrappers/ErrorStatus';
import {
  IAny,
  IDetailComponentProps,
  IEmaasEnergyUsageComponentData,
  IEmaasEnergyUsageComponentDataArea
} from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import EmaasEnergyUsagesAllUtilityDetailsComponent from './EmaasEnergyUsagesAllUtilityDetailsComponent';
import EmaasNILMEnergyUsagesComponent from '../NILMEnergyUsage/EmaasNILMEnergyUsagesComponent';
import { Card, Icon, Tooltip } from '@scuf/common';
import EmaasEUsageAllUtilDetailsBasicComponent from './EmaasEUsageAllUtilDetailsBasicComponent';
import {
  EmaasSubscriptionType,
  EnergyUsageDataSourceType,
  EnergyUsageField
} from '../../../shared/Enums';
import EmaasNILMEnergyDisaggregationComponent from '../EnergyDisaggregationDetails/EmaasNILMEnergyDisaggregationComponent';
import { ApiContext } from '../../../../context/ApiContext';

type IEnergyData =
  | IEmaasEnergyUsageComponentData
  | IEmaasEnergyUsageComponentDataArea;

interface ITotalEmissionTabDetailsComponentProps {
  data: IEnergyData;
  onRefresh: () => Promise<void>;
  nilmTab?: string;
  forGraph?: boolean;
  forSummary?: boolean;
  checkAllSitesAndMonthData?: boolean;
}

const DetermineNILM = (props: IAny) => {
  const { data, nilmTab } = props;
  return data.subscriptionType === EnergyUsageField.BASICSUBS &&
    data.addOn.includes('NILM') &&
    nilmTab === EnergyUsageField.NILMENERGYUSAGE ? (
    <EmaasNILMEnergyUsagesComponent data={data} />
  ) : (
    <EmaasEnergyUsagesAllUtilityDetailsComponent
      data={data as IEmaasEnergyUsageComponentDataArea}
      forGraph={props.forGraph}
      forSummary={props.forSummary}
    />
  );
};

const SiteEnergyAllUtilitiesDetailContent = (
  props: ITotalEmissionTabDetailsComponentProps
) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const {
    data,
    onRefresh,
    nilmTab,
    forGraph,
    forSummary,
    checkAllSitesAndMonthData
  } = props;
  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }
  return (
    <div className="carbondetail-performance-detail-container">
      <div>
        {data.subscriptionType === EnergyUsageField.BASICSUBS && !data.addOn ? (
          <EmaasEUsageAllUtilDetailsBasicComponent
            data={data}
            checkAllSitesAndMonthData={checkAllSitesAndMonthData}
          />
        ) : (
          <DetermineNILM
            data={data}
            nilmTab={nilmTab}
            forGraph={forGraph}
            forSummary={forSummary}
          />
        )}
        {/* {data.subscriptionType === EnergyUsageField.BASICSUBS && !data.addOn ? (
          <EmaasEUsageAllUtilDetailsBasicComponent data={data} />
        ) : data.subscriptionType === EnergyUsageField.BASICSUBS &&
          data.addOn &&
          data.addOn.includes('NILM') &&
          nilmTab === EnergyUsageField.NILMENERGYUSAGE ? (
          <EmaasNILMEnergyUsagesComponent data={data} />
        ) : (
          <EmaasEnergyUsagesAllUtilityDetailsComponent
            data={data as IEmaasEnergyUsageComponentDataArea}
          />
        )} */}
      </div>
    </div>
  );
};

const SiteEnergyAllUtilitiesDetailWithLoader = withLoadingStatus(
  withErrorStatus(SiteEnergyAllUtilitiesDetailContent)
);

const emaasEnergyUsagesAllUtilityDetailWidget = (
  props: IDetailComponentProps
) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const { dataConnector, nilmTab, requestContext } = props;

  const apiContext = useContext(ApiContext);
  const { dataSourceType } = apiContext;

  const onRefresh = () =>
    dataConnector && dataConnector.actions && dataConnector.actions.getAll();
  const { all, loading, error, status } = dataConnector.data;
  const electricitySavings = (all as unknown) as IEnergyData;
  const isDataAvailable = electricitySavings ? true : false;

  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [statusCode, setStatusCode] = useState<number | null>(0);
  const [errorInfo, setErrorInfo] = useState(error);
  const [data, setData] = useState({} as IEnergyData);
  const [checkAllSitesAndMonthData, setCheckAllSitesAndMonthData] = useState<
    boolean
  >(true);

  // const checkIfAllSitesHasCompleteData = (data: any) => {
  //   return (
  //     data &&
  //     // data.breakdownBySite &&
  //     data.breakdownBySite.every(
  //       (item: { isCompleted: boolean }) => item.isCompleted
  //     )
  //   );
  // };

  const checkIfAllMonthHasCompleteData = (data: any) => {
    return (
      data &&
      data.monthWiseAggregations &&
      (data.subscriptionType !== EnergyUsageField.BASICSUBS ||
        data.monthWiseAggregations.every(
          (item: { isCompleted: boolean }) => item.isCompleted
        ))
    );
  };

  const checkIfAllMonthsHasPreviousPeriod = (data: any) => {
    return (
      data &&
      data.monthWiseAggregations &&
      data.monthWiseAggregations.every(
        (item: {
          totalBaselineConsumption: number | null;
          totalConsumption: number | null;
        }) =>
          item.totalBaselineConsumption !== 0 &&
          item.totalBaselineConsumption !== null &&
          item.totalConsumption !== 0 &&
          item.totalConsumption !== null
      )
    );
  };

  // const checkIfAllSitesHasPreviousPeriod = (data: any) => {
  //   return (
  //     data &&
  //     // data.breakdownBySite &&
  //     data.breakdownBySite.every(
  //       (item: {
  //         totalBaselineConsumption: number | null;
  //         totalConsumption: number | null;
  //       }) =>
  //         item.totalConsumption !== 0 &&
  //         item.totalBaselineConsumption !== 0 &&
  //         item.totalConsumption !== null &&
  //         item.totalBaselineConsumption !== null
  //     )
  //   );
  // };

  useEffect(() => {
    setIsLoading(loading);
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        setCheckAllSitesAndMonthData(
          // checkIfAllSitesHasPreviousPeriod(electricitySavings) &&
          checkIfAllMonthsHasPreviousPeriod(electricitySavings) &&
            checkIfAllMonthHasCompleteData(electricitySavings)
          // checkIfAllSitesHasCompleteData(electricitySavings)
        );
        setData(electricitySavings);
      }
      setErrorInfo(error);
      setStatusCode(status);
    }
  }, [isDataAvailable, loading, status]);

  return (
    <>
      <Card className="emaas-performance-detail-card">
        <Card.Header>
          <div className="emaas-summary-card-header">
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span>{nilmTab ? t(d.NILMENERGYUSAGE) : props.headerTitle}</span>
              {nilmTab && (
                <span className="NILM-energy-usage-subheader">
                  {t(d.NILMENERGYUSAGESUBHEADER)}
                </span>
              )}
            </div>
            {!nilmTab && (
              <Tooltip
                element={
                  <Icon
                    root="common"
                    name="badge-info"
                    exactSize="0.875rem"
                    className="emaas-summary-card-header-info-icon"
                  />
                }
                event="hover"
                // content={t(d.EMAASENERGYUSAGETOOLTIP)}
                content={
                  data.subscriptionType === EnergyUsageField.BASICSUBS ||
                  dataSourceType === EnergyUsageDataSourceType.UTILITY_METER
                    ? t(d.CEMSPORTFOLIO_DATA_SOURCE_UTIL_TOOLTIP)
                    : props.headerTitle === t(d.WaterOverview)
                    ? t(d.EMAASWATEROVERVIEWTOOLTIP)
                    : t(d.EMAASENERGYUSAGETOOLTIP)
                }
                position="right center"
                size="small"
                header={props.headerTitle}
              />
            )}
          </div>
        </Card.Header>
        <Card.Content className="detail-summary-card detail-view">
          <SiteEnergyAllUtilitiesDetailWithLoader
            loading={isLoading}
            error={errorInfo}
            statusCode={statusCode}
            onRefresh={onRefresh}
            data={data}
            loadingText={t(d.LOADING)}
            title={t(d.CONNECTIONERRORTITLE)}
            noDataMessage={t(d.NODATAAVAILABLE)}
            connectionErrorMessage={t(d.CONNECTIONERROR)}
            nilmTab={nilmTab}
            forGraph={true}
            forSummary={true}
            checkAllSitesAndMonthData={checkAllSitesAndMonthData}
          />
        </Card.Content>
      </Card>
    </>
  );
};
export default emaasEnergyUsagesAllUtilityDetailWidget;
